/**
 * Contra Amount Calculator for trade booking
 * Calculates the contra amount based on market convention
 */

// Currencies with zero decimal places (no fractional units)
const ZERO_DECIMAL_CURRENCIES = new Set(['CLP', 'JPY', 'KRW', 'VND', 'PYG']);

/**
 * Calculate contra amount from amount and price
 * Formula: Contra Amount = Amount × Price (always multiply, following market convention)
 *
 * When currency2 is a zero-decimal currency (e.g. CLP), the result is rounded
 * to the nearest whole unit.
 *
 * Examples:
 * - USD/CLP: 1000 USD × 950.25 = 950,250 CLP (rounded to whole peso)
 * - EUR/USD: 1000 EUR × 1.10 = 1,100.00 USD (2 decimal places)
 *
 * @param amount - The notional amount in Currency 1
 * @param price - The exchange rate (Currency 2 per Currency 1)
 * @param currency2 - Optional ISO currency code for Currency 2 (used for rounding)
 * @returns Contra amount in Currency 2, or null if inputs are invalid
 */
export const calculateContraAmount = (
  amount: number | null,
  price: number | null,
  currency2?: string,
): number | null => {
  if (amount === null || price === null) {
    return null;
  }

  if (isNaN(amount) || isNaN(price)) {
    return null;
  }

  if (amount === 0 || price === 0) {
    return 0;
  }

  const result = amount * price;
  if (currency2 && ZERO_DECIMAL_CURRENCIES.has(currency2.toUpperCase())) {
    return Math.round(result);
  }
  return result;
};

/**
 * Format number for display with appropriate decimal places
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export const formatAmount = (value: number | null, decimals: number = 2): string => {
  if (value === null) {
    return '';
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Round number to specified decimal places
 * @param value - Number to round
 * @param decimals - Number of decimal places
 * @returns Rounded number
 */
export const roundToDecimals = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};
